import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Printer, X, History, Calendar } from "lucide-react";
import Layout from "./layout";
import TemplateDetailModal from "../Modal/TemplateDetailModal";
import EditTemplateModal from "../Modal/editTamplateModal";
import HealthResultModal from "../Modal/HealthResultModal";
import SendHealthResultModal from "../Modal/SendHealthResultModal";
import HealthReportModal from "../Modal/HealthReportModal";
import { useNavigate } from "react-router-dom";
import { healthRecordsAPI, templateAPI, normalRangeAPI, healthRecommendationAPI } from "../services/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { createGlobalStyle } from 'styled-components';

// Custom styles สำหรับ DatePicker
const DatePickerStyles = createGlobalStyle`
  .react-datepicker {
    font-family: inherit;
    border: 2px solid #00B5B8;
    border-radius: 0.5rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  .react-datepicker__header {
    background-color: #00B5B8;
    border-bottom: none;
    border-radius: 0.5rem 0.5rem 0 0;
    padding: 1rem 0.75rem 0.75rem;
  }

  .react-datepicker__current-month {
    color: #FFFFFF;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .react-datepicker__day-name {
    color: #FFFFFF;
    font-size: 0.875rem;
    font-weight: 600;
    width: 2rem;
    line-height: 2rem;
    margin: 0.166rem;
  }

  .react-datepicker__day {
    color: #1F2937;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 0.375rem;
    width: 2rem;
    line-height: 2rem;
    margin: 0.166rem;
    transition: all 0.2s;

    &:hover {
      background-color: #00B5B8;
      color: white;
      transform: scale(1.1);
    }
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: #00B5B8;
    color: white;
    font-weight: 700;
    border: 2px solid #00B5B8;

    &:hover {
      background-color: #008F92;
      border-color: #008F92;
    }
  }

  .react-datepicker__day--disabled {
    color: #D1D5DB;
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background-color: transparent;
      transform: none;
    }
  }

  .react-datepicker__day--today {
    font-weight: 700;
    color: #00B5B8;
    background-color: #E0F2F3;
    border: 2px solid #00B5B8;

    &:hover {
      background-color: #00B5B8;
      color: white;
    }
  }

  .react-datepicker__day--outside-month {
    color: #9CA3AF;
    opacity: 0.5;
  }

  .react-datepicker__month-dropdown,
  .react-datepicker__year-dropdown {
    background-color: white;
    border: 1px solid #E5E7EB;
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .react-datepicker__month-option,
  .react-datepicker__year-option {
    font-size: 0.875rem;
    padding: 0.5rem;

    &:hover {
      background-color: #E0F2F3;
      color: #00B5B8;
    }
  }

  .react-datepicker__month-option--selected,
  .react-datepicker__year-option--selected {
    background-color: #00B5B8;
    color: white;
    font-weight: 600;
  }

  .react-datepicker__navigation {
    top: 1rem;
  }

  .react-datepicker__navigation-icon::before {
    border-color: white;
    border-width: 2px 2px 0 0;
    height: 7px;
    width: 7px;
  }

  .react-datepicker__month-container {
    padding: 0.5rem;
  }
`;

const PageContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 400;
  color: #111827;
  margin-bottom: 2rem;
`;

const FilterSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr auto auto;
  grid-template-rows: repeat(2, auto);
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: end;
`;

const GridItem = styled.div`
  grid-column: ${props => props.column};
  grid-row: ${props => props.row};
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const DateInputContainer = styled.div`
  position: relative;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  pointer-events: none;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  
  &::placeholder {
    color: #9CA3AF;
  }
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SearchButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #0891B2;
  }
`;

const PrintButton = styled.button`
  background-color: #F9FAFB;
  color: #374151;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const TemplateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${props => props.disabled ? '#F9FAFB' : 'transparent'};
  border: 1px solid ${props => props.disabled ? '#D1D5DB' : '#00B5B8'};
  color: ${props => props.disabled ? '#9CA3AF' : '#00B5B8'};
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: 30px;
  width: auto;
  position: relative;
  cursor: pointer;
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

const TemplateBadgeText = styled.span`
  margin-right: 0.5rem;
`;

const RemoveButton = styled.button`
  background: ${props => props.disabled ? '#F3F4F6' : 'white'};
  border: 1px solid ${props => props.disabled ? '#D1D5DB' : '#00B5B8'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  color: ${props => props.disabled ? '#9CA3AF' : '#00B5B8'};
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: auto;

  &:hover {
    background: ${props => props.disabled ? '#F3F4F6' : '#F9FAFB'};
  }
`;

const ViewDataButton = styled.button`
  background-color: #FBFFFF;
  color: #9c9e9eff;
  border: 1px solid #CFD3D4;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 0.5rem;
  width: auto;
  white-space: nowrap;
  
  &:hover {
    background-color: #F3F4F6;
  }
`;

const StatusText = styled.div`
  font-size: 0.75rem;
  color: #374151;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const StatusBadge = styled.div`
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 0.5rem;
  text-align: center;
  width: 100%;
  text-decoration: underline;
  background: none;
  border: none;
  color: ${props => 
    props.status === 'รอการอนุมัติ' ? '#FF0404' :
    props.status === 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว' ? '#00B5B8' :
    props.status === 'มีการแก้ไขรอการอนุมัติอีกครั้ง' ? '#FF0404' : '#6F6F6F'
  };
`;

const ActionPrintButton = styled.button`
  background: none;
  color: ${props => props.disabled ? '#9CA3AF' : '#374151'};
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    color: ${props => props.disabled ? '#9CA3AF' : '#00B5B8'};
  }
`;

const EditButton = styled.button`
  background-color: ${props => props.disabled ? '#F3F4F6' : '#FFFFFF'};
  color: ${props => props.disabled ? '#9CA3AF' : '#374151'};
  border: 1px solid ${props => props.disabled ? '#E5E7EB' : '#CFD3D4'};
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin: 0.125rem;

  &:hover {
    background-color: ${props => props.disabled ? '#F3F4F6' : '#F9FAFB'};
  }
`;

const ViewHiddenButton = styled.button`
  background-color: #FFF7ED;
  color: #EA580C;
  border: 1px solid #FDBA74;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #FFEDD5;
    border-color: #FB923C;
  }
`;

const SendResultButton = styled.button`
  background-color: #00B5B8;
  color: #FFFFFF;
  border: 1px solid #00B5B8;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin: 0.125rem;

  &:hover {
    background-color: #0891B2;
    border-color: #0891B2;
  }
`;

const ResendButton = styled.button`
  background-color: #1D87F0;
  color: #FFFFFF;
  border: 1px solid #1D87F0;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin: 0.125rem;

  &:hover {
    background-color: #1565C0;
    border-color: #1565C0;
  }
`;

const HistoryButton = styled.button`
  background: none;
  border: none;
  color: #6F6F6F;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  text-decoration: underline;

  &:hover {
    color: #6F6F6F;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 2rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #F9FAFB;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;

  &:hover {
    background-color: #F9FAFB;
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #A3A3A3;
  text-align: center;
`;

const TableHeaderCell2 = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #A3A3A3;
  text-align: center;
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 14px;
  color: #6F6F6F;
  text-align: center;
  vertical-align: top;
`;

const ComparisonBadge = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
  background-color: transparent;
  color: ${props => {
    if (props.status === 'high') return '#DC2626';
    if (props.status === 'low') return '#D97706';
    return '#059669';
  }};
`;

const HealthBook = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchCompany, setSearchCompany] = useState("");
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHealthResultModalOpen, setIsHealthResultModalOpen] = useState(false);
  const [isSendHealthResultModalOpen, setIsSendHealthResultModalOpen] = useState(false);
  const [isHealthReportModalOpen, setIsHealthReportModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isHiddenTemplatesModalOpen, setIsHiddenTemplatesModalOpen] = useState(false);
  const [hiddenTemplates, setHiddenTemplates] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const navigate = useNavigate();

  // แปลง Date object เป็น YYYY-MM-DD
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSearch = () => {
    // ถ้าไม่มีการกรองอะไรเลย แสดงข้อมูลทั้งหมด (Auto Reset)
    if (!startDate && !endDate && !searchText && !searchCompany && !status) {
      setHealthRecords(allHealthRecords);
      return;
    }

    let filtered = [...allHealthRecords];

    // แปลงวันที่จาก DD-MM-YYYY เป็น YYYY-MM-DD เพื่อเปรียบเทียบ
    const convertToYYYYMMDD = (dateStr) => {
      if (!dateStr || dateStr === '-') return null;
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        // DD-MM-YYYY -> YYYY-MM-DD
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
      return dateStr;
    };

    // 1. Filter ตามวันที่เริ่ม
    if (startDate) {
      filtered = filtered.filter(record => {
        if (!record.date || record.date === '-') return false;
        const recordDateYYYYMMDD = convertToYYYYMMDD(record.date);
        // แปลง startDate (Date object) เป็น YYYY-MM-DD
        const startDateYYYYMMDD = formatDateToYYYYMMDD(startDate);
        if (!startDateYYYYMMDD) return true;
        return recordDateYYYYMMDD >= startDateYYYYMMDD;
      });
    }

    // 2. Filter ตามวันที่สิ้นสุด
    if (endDate) {
      filtered = filtered.filter(record => {
        if (!record.date || record.date === '-') return false;
        const recordDateYYYYMMDD = convertToYYYYMMDD(record.date);
        // แปลง endDate (Date object) เป็น YYYY-MM-DD
        const endDateYYYYMMDD = formatDateToYYYYMMDD(endDate);
        if (!endDateYYYYMMDD) return true;
        return recordDateYYYYMMDD <= endDateYYYYMMDD;
      });
    }

    // 3. Filter ตามชื่อ-สกุล หรือ HN (ค้นหาใน patientName และ hn)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(record => {
        const nameMatch = record.patientName.toLowerCase().includes(searchLower);
        const hnMatch = record.hn.toLowerCase().includes(searchLower);
        return nameMatch || hnMatch;
      });
    }

    // 4. Filter ตามบริษัท (ถ้ามีข้อมูล company ใน healthData)
    if (searchCompany) {
      const companyLower = searchCompany.toLowerCase();
      filtered = filtered.filter(record => {
        // สมมติว่ามีข้อมูล company ใน record
        const company = record.company || '';
        return company.toLowerCase().includes(companyLower);
      });
    }

    // 5. Filter ตามสถานะ
    if (status) {
      // แปลงข้อความสถานะเป็น approval_status enum
      let statusEnum = 'pending';
      if (status === 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว') {
        statusEnum = 'approved';
      } else if (status === 'มีการแก้ไขรอการอนุมัติอีกครั้ง') {
        statusEnum = 'revised';
      }
      filtered = filtered.filter(record => record.approvalStatus === statusEnum);
    }

    // อัพเดทข้อมูลที่แสดง
    setHealthRecords(filtered);
  };

  const handlePrint = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setIsHealthReportModalOpen(true);
    }
  };

  const handleViewData = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setIsHealthReportModalOpen(true);
    }
  };

  const handleRemoveTemplate = async (recordId, templateIndex) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (!record) return;

    const templateName = record.templates[templateIndex];
    const matchedTemplate = record.matchedTemplates?.find(t => t.template_name === templateName);

    if (!matchedTemplate || !matchedTemplate.template_id) {
      console.error('Cannot find template ID for:', templateName);
      return;
    }

    try {
      // เรียก API เพื่อซ่อน template (persist ลง database)
      await healthRecordsAPI.hideTemplate(
        recordId,
        matchedTemplate.template_id,
        templateName
      );

      // อัพเดท local state หลังจาก API สำเร็จ
      const updatedRecords = healthRecords.map(r => {
        if (r.id === recordId) {
          const updatedTemplates = r.templates.filter((_, index) => index !== templateIndex);
          const updatedMatchedTemplates = r.matchedTemplates?.filter((_, index) => index !== templateIndex);
          const updatedTags = [];
          updatedMatchedTemplates?.forEach(t => {
            updatedTags.push(...(t.tags || []));
          });

          return {
            ...r,
            templates: updatedTemplates,
            matchedTemplates: updatedMatchedTemplates,
            tags: [...new Set(updatedTags)]
          };
        }
        return r;
      });

      setHealthRecords(updatedRecords);
      setAllHealthRecords(updatedRecords);

    } catch (error) {
      console.error('❌ Error hiding template:', error);
    }
  };

  const handleViewHiddenTemplates = async (recordId) => {
    try {
      setSelectedRecordId(recordId);
      const response = await healthRecordsAPI.getHiddenTemplates(recordId);

      if (response.status === 'success' && Array.isArray(response.data)) {
        setHiddenTemplates(response.data);
        setIsHiddenTemplatesModalOpen(true);
      } else {
        console.error('❌ ไม่สามารถดึงข้อมูล Templates ที่ซ่อนได้');
      }
    } catch (error) {
      console.error('❌ Error fetching hidden templates:', error);
    }
  };

  const handleRestoreTemplate = async (templateId) => {
    if (!selectedRecordId) return;

    try {
      await healthRecordsAPI.unhideTemplate(selectedRecordId, templateId);

      // รีโหลดข้อมูล health records
      await fetchHealthRecords();

      // รีโหลดรายการ hidden templates
      const response = await healthRecordsAPI.getHiddenTemplates(selectedRecordId);
      if (response.status === 'success' && Array.isArray(response.data)) {
        setHiddenTemplates(response.data);

        // ถ้าไม่มี hidden templates แล้ว ปิด modal
        if (response.data.length === 0) {
          setIsHiddenTemplatesModalOpen(false);
        }
      }

    } catch (error) {
      console.error('❌ Error restoring template:', error);
    }
  };

  const handleEdit = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setSelectedRecordId(recordId);
      setIsEditModalOpen(true);
    }
  };

  const handleSendResult = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      if (record.isApproved) {
        // ถ้าเป็นการส่งใหม่ แสดง SendHealthResultModal
        setIsSendHealthResultModalOpen(true);
      } else {
        // ถ้าเป็นการส่งครั้งแรก แสดง HealthResultModal
        setIsHealthResultModalOpen(true);
      }
    }
  };

  const handleTemplateClick = async (templateName, matchedTemplateData = null) => {
    try {
      let template = null;

      // ถ้ามี matchedTemplateData ใช้เลย แต่ต้อง fetch รายละเอียดเพิ่ม
      if (matchedTemplateData && matchedTemplateData.template_id) {
        const response = await templateAPI.getById(matchedTemplateData.template_id);
        if (response.status === 'success' && response.data) {
          template = response.data;
        }
      } else {
        // ถ้าไม่มี ให้ค้นหาจาก template name
        const response = await templateAPI.getAll();
        if (response.status === 'success' && response.data) {
          template = response.data.find(t => t.template_name === templateName);
        }
      }

      if (template) {
        // แปลงข้อมูลให้ตรงกับ format ที่ TemplateDetailModal ต้องการ
        const formattedTemplate = {
          templateName: template.template_name,
          createdBy: template.created_by,
          tags: template.tags || [],
          gender: template.gender === 'male' ? 'ชาย' : template.gender === 'female' ? 'หญิง' : 'ทั้งสองเพศ',
          ageRange: `${template.age_min}-${template.age_max}`,
          parameters: template.parameters?.map(p => p.parameter_name) || [],
          normalValues: template.parameters?.map(p => p.normal_range || '-') || [],
          setValues: template.parameters?.map(p => `${p.custom_min}-${p.custom_max}`) || []
        };

        setSelectedTemplate(formattedTemplate);
        setIsModalOpen(true);
      } else {
        console.warn('Template not found:', templateName);
      }
    } catch (error) {
      console.error('Error loading template:', error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    // รีเฟรชข้อมูลหลังปิด modal เพื่อให้แสดง tags ที่อัพเดทแล้ว
    fetchHealthRecords();
  };

  const handleCloseHealthResultModal = () => {
    setIsHealthResultModalOpen(false);
    setSelectedRecord(null);
  };

  const handleApprove = async (recordId) => {
    // Reload ข้อมูลใหม่จาก database เพื่อให้ได้ approval_status ที่ถูกต้อง
    await fetchHealthRecords();
  };

  const handleViewHistory = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      navigate('/health-records-history', { state: { selectedRecord: record } });
    }
  };

  const handleCloseSendHealthResultModal = () => {
    setIsSendHealthResultModalOpen(false);
    setSelectedRecord(null);
  };

  const handleConfirmSendAgain = async () => {
    // เปลี่ยนสถานะเป็น 'revised' โดยการบันทึก draft ใหม่
    // Backend จะตรวจสอบว่าถ้าเคย approved แล้วจะเปลี่ยนเป็น revised อัตโนมัติ
    if (selectedRecord) {
      try {
        console.log('🔄 DEBUG - Sending request to change status to revised');
        console.log('🔄 DEBUG - Record ID:', selectedRecord.id);
        console.log('🔄 DEBUG - Current approval status:', selectedRecord.approvalStatus);

        // โหลดข้อมูล recommendations ปัจจุบัน
        const response = await healthRecommendationAPI.getDraft(selectedRecord.id);
        console.log('🔄 DEBUG - getDraft response:', response);

        if (response.status === 'success' && response.data) {
          // บันทึก draft เพื่อให้ backend เปลี่ยนสถานะเป็น revised
          const saveResponse = await healthRecommendationAPI.saveDraft(
            selectedRecord.id,
            {
              doctor_advice: response.data.doctor_advice || '',
              nutrition_advice: response.data.nutrition_advice || '',
              exercise_advice: response.data.exercise_advice || '',
              sleep_advice: response.data.sleep_advice || ''
            },
            'admin'
          );
          console.log('🔄 DEBUG - saveDraft response:', saveResponse);

          // Reload ข้อมูลใหม่เพื่อแสดงสถานะที่อัพเดท
          console.log('🔄 DEBUG - Reloading health records...');
          await fetchHealthRecords();
          console.log('🔄 DEBUG - Health records reloaded');
        }
      } catch (error) {
        console.error('❌ Error changing status to revised:', error);
      }
    }
    setIsSendHealthResultModalOpen(false);
    setIsHealthResultModalOpen(true);
  };

  const handleCloseHealthReportModal = () => {
    setIsHealthReportModalOpen(false);
    setSelectedRecord(null);
  };

  // State สำหรับเก็บข้อมูลจาก API
  const [healthRecords, setHealthRecords] = useState([]);
  const [allHealthRecords, setAllHealthRecords] = useState([]); // เก็บข้อมูลทั้งหมดไว้สำหรับ filter
  const [loading, setLoading] = useState(false);

  // ฟังก์ชันเปรียบเทียบค่าปกติกับค่าที่ตรวจวัดได้
  const compareValues = (normalRange, resultValue) => {
    if (!normalRange || !resultValue || normalRange === 'xxx' || resultValue === 'xxx' || normalRange === '-' || resultValue === '-') {
      return { status: 'normal', label: '-' };
    }

    // แปลงค่าที่ตรวจวัดได้เป็นตัวเลข
    const result = parseFloat(resultValue);
    if (isNaN(result)) {
      return { status: 'normal', label: '-' };
    }

    // แยกช่วงค่าปกติ (เช่น "100-200" หรือ "<100" หรือ ">100")
    if (normalRange.includes('-')) {
      const [min, max] = normalRange.split('-').map(v => parseFloat(v.trim()));
      if (result < min) {
        return { status: 'low', label: 'Low' };
      } else if (result > max) {
        return { status: 'high', label: 'High' };
      } else {
        return { status: 'normal', label: 'Normal' };
      }
    } else if (normalRange.startsWith('<')) {
      const max = parseFloat(normalRange.replace('<', '').trim());
      if (result >= max) {
        return { status: 'high', label: 'High' };
      } else {
        return { status: 'normal', label: 'Normal' };
      }
    } else if (normalRange.startsWith('>')) {
      const min = parseFloat(normalRange.replace('>', '').trim());
      if (result <= min) {
        return { status: 'low', label: 'Low' };
      } else {
        return { status: 'normal', label: 'Normal' };
      }
    }

    return { status: 'normal', label: 'Normal' };
  };

  // ดึงข้อมูลจาก Database เมื่อ component โหลด
  useEffect(() => {
    fetchHealthRecords();
  }, []);

  // Auto-refresh เมื่อกลับมาที่หน้านี้ (เช่น หลังจากไปปิด template แล้วกลับมา)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // เมื่อ tab กลับมา active ให้ reload ข้อมูล
        fetchHealthRecords();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchHealthRecords = async () => {
    try {
      setLoading(true);
      const response = await healthRecordsAPI.getAll();

      if (response.status === 'success' && Array.isArray(response.data)) {
        // แปลงข้อมูลจาก Database ให้ตรงกับรูปแบบที่ UI ใช้
        const formattedData = await Promise.all(response.data.map(async (record, index) => {
          const healthData = record.health_data || {};

          // ดึงข้อมูล Lab Results (อยู่ใน health_data.health_data[0])
          const labData = Array.isArray(healthData.health_data) && healthData.health_data.length > 0
            ? healthData.health_data[0]
            : {};


          // ดึงอายุและเพศสำหรับ fallback ค่าปกติ
          const age = labData['age'] || 0;
          const sex = labData['sex'] === 'ชาย' ? 'M' : 'F';

          // แปลงข้อมูล Lab Results เป็น parameters สำหรับแสดงในตาราง
          const parameters = [];
          const testsNeedingNormalRange = []; // เก็บรายการที่ต้องดึงค่าปกติจาก medium.json

          // CBC - Complete Blood Count
          // ✅ ไม่ใช้ค่าปกติจาก API response แต่ดึงจาก medium.json เท่านั้น
          if (labData['cbc rbc result']) {
            const param = {
              name: 'RBC',
              result: labData['cbc rbc result'],
              normal: null, // ดึงจาก medium.json
              unit: null // ดึงจาก medium.json
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'RBC', sex, age });
          }

          if (labData['cbc wbc result']) {
            const param = {
              name: 'WBC',
              result: labData['cbc wbc result'],
              normal: null, // ดึงจาก medium.json
              unit: null // ดึงจาก medium.json
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'WBC', sex, age });
          }

          if (labData['cbc hb result']) {
            const param = {
              name: 'Hemoglobin',
              result: labData['cbc hb result'],
              normal: null, // ดึงจาก medium.json
              unit: null // ดึงจาก medium.json
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Hemoglobin', sex, age });
          }

          if (labData['cbc hct result']) {
            const param = {
              name: 'Hematocrit',
              result: labData['cbc hct result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Hematocrit', sex, age });
            }
          }

          if (labData['cbc mcv result']) {
            const param = {
              name: 'MCV',
              result: labData['cbc mcv result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'MCV', sex, age });
            }
          }

          if (labData['cbc mch result']) {
            const param = {
              name: 'MCH',
              result: labData['cbc mch result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'MCH', sex, age });
            }
          }

          if (labData['cbc mchc result']) {
            const param = {
              name: 'MCHC',
              result: labData['cbc mchc result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'MCHC', sex, age });
            }
          }

          if (labData['cbc plt count result']) {
            const param = {
              name: 'Platelets',
              result: labData['cbc plt count result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Platelets', sex, age });
            }
          }

          if (labData['cbc neutrophile result']) {
            const param = {
              name: 'Neutrophile',
              result: labData['cbc neutrophile result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Neutrophile', sex, age });
            }
          }

          if (labData['cbc lymphocyte result']) {
            const param = {
              name: 'Lymphocyte',
              result: labData['cbc lymphocyte result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Lymphocyte', sex, age });
            }
          }

          if (labData['cbc monocyte result']) {
            const param = {
              name: 'Monocyte',
              result: labData['cbc monocyte result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Monocyte', sex, age });
            }
          }

          // Kidney Function Tests
          if (labData['creatinine creatinine result']) {
            const param = {
              name: 'Creatinine',
              result: labData['creatinine creatinine result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Creatinine', sex, age });
            }
          }

          // BUN (Blood Urea Nitrogen)
          if (labData['bun result']) {
            const param = {
              name: 'BUN, Urea',
              result: labData['bun result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'BUN, Urea', sex, age });
            }
          }

          if (labData['creatinine egfr result']) {
            const param = {
              name: 'eGFR',
              result: labData['creatinine egfr result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'eGFR', sex, age });
            }
          }

          if (labData['uric acid result']) {
            const param = {
              name: 'Uric Acid',
              result: labData['uric acid result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Uric Acid', sex, age });
            }
          }

          // Diabetes Tests
          if (labData['glucose result']) {
            const param = {
              name: 'Glucose',
              result: labData['glucose result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Glucose', sex, age });
            }
          }

          if (labData['hba1c hb a1c result']) {
            const param = {
              name: 'HbA1C',
              result: labData['hba1c hb a1c result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'HbA1C', sex, age });
            }
          }

          // Liver Function Tests
          if (labData['sgpt result']) {
            const param = {
              name: 'SGPT (ALT)',
              result: labData['sgpt result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'SGPT (ALT)', sex, age });
            }
          }

          if (labData['liver function test sgot ast result']) {
            const param = {
              name: 'SGOT (AST)',
              result: labData['liver function test sgot ast result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'SGOT (AST)', sex, age });
            }
          }

          if (labData['liver function test alkaline phosphatase result']) {
            const param = {
              name: 'ALP (Alkaline Phosphatase)',
              result: labData['liver function test alkaline phosphatase result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'ALP', sex, age });
            }
          }

          if (labData['liver function test albumin result']) {
            const param = {
              name: 'Albumin',
              result: labData['liver function test albumin result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Albumin', sex, age });
            }
          }

          if (labData['liver function test total protein result']) {
            const param = {
              name: 'Total Protein',
              result: labData['liver function test total protein result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Total Protein', sex, age });
            }
          }

          if (labData['liver function test globulin result']) {
            const param = {
              name: 'Globulin',
              result: labData['liver function test globulin result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Globulin', sex, age });
            }
          }

          if (labData['liver function test bilirubin total result']) {
            const param = {
              name: 'Total Bilirubin',
              result: labData['liver function test bilirubin total result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Total Bilirubin', sex, age });
            }
          }

          if (labData['ggt result']) {
            const param = {
              name: 'GGT',
              result: labData['ggt result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'GGT', sex, age });
            }
          }

          // Lipid Profile
          if (labData['hdl result']) {
            const param = {
              name: 'HDL',
              result: labData['hdl result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'HDL', sex, age });
            }
          }

          if (labData['ldl result']) {
            const param = {
              name: 'LDL',
              result: labData['ldl result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'LDL', sex, age });
            }
          }

          if (labData['total cholesterol result']) {
            const param = {
              name: 'คอเลสเตอรอลรวม',
              result: labData['total cholesterol result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'คอเลสเตอรอลรวม', sex, age });
            }
          }

          // ลองหา triglyceride ทุกรูปแบบ
          const trigFieldVariations = [
            'triglyceride result',
            'Triglyceride result',
            'ไตรกลีเซอไรด์ result',
            'triglyceride',
            'Triglyceride'
          ];

          let trigValue = null;
          let trigFieldUsed = null;

          for (const field of trigFieldVariations) {
            if (labData[field]) {
              trigValue = labData[field];
              trigFieldUsed = field;
              break;
            }
          }

          if (trigValue) {
            const param = {
              name: 'ไตรกลีเซอไรด์',
              result: trigValue,
              normal: null,
              unit: null
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'ไตรกลีเซอไรด์', sex, age });
          }

          // Urinalysis
          if (labData['urinalysis sugar result']) {
            const param = {
              name: 'Glucose',
              result: labData['urinalysis sugar result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Glucose', sex, age });
            }
          }

          if (labData['urinalysis protein result']) {
            const param = {
              name: 'Protein',
              result: labData['urinalysis protein result'],
              normal: null,
              unit: null
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Protein', sex, age });
            }
          }

          // ✅ ดึงค่าปกติจาก medium.json สำหรับทุก parameters (ไม่ใช้ค่าจาก DB)
          if (testsNeedingNormalRange.length > 0) {
            try {
              const testsForAPI = testsNeedingNormalRange.map(t => ({
                name: t.name,
                sex: t.sex,
                age: t.age
              }));

              const normalRangeResponse = await normalRangeAPI.getBatch(testsForAPI);

              if (normalRangeResponse.status === 'success' && normalRangeResponse.data) {
                // อัพเดทค่าปกติและหน่วยที่ดึงจาก medium.json
                normalRangeResponse.data.forEach(result => {
                  if (result.normal_range && !result.error) {
                    const test = testsNeedingNormalRange.find(t => t.name === result.test_name);
                    if (test && test.param) {
                      test.param.normal = result.normal_range;
                      test.param.unit = result.unit || ''; // เพิ่มการบันทึกหน่วย
                    }
                  }
                });
              }
            } catch (error) {
              console.error('Error fetching normal ranges from medium.json:', error);
            }
          }

          // ใส่ค่า default '-' สำหรับ parameters ที่ยังไม่มีค่าปกติ
          parameters.forEach(param => {
            if (!param.normal) {
              param.normal = '-';
            }
          });


          // ถ้าไม่มี parameters จาก Lab ให้ใช้จาก healthData.parameters (ถ้ามี)
          if (parameters.length === 0 && healthData.parameters) {
            parameters.push(...(healthData.parameters || []).map(param => ({
              name: param.name || '',
              result: param.value || '-',
              normal: param.normal || '-'
            })));
          }

          // แปลงวันที่จาก YYYY-MM-DD เป็น DD-MM-YYYY
          const formatDate = (dateString) => {
            if (!dateString || dateString === '-') return '-';
            const parts = dateString.split('-');
            if (parts.length === 3) {
              return `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
            return dateString;
          };

          // โหลด approval_status และคำแนะนำจาก health_recommendations
          let approvalStatus = 'pending';
          let approvedAt = null;
          let nutritionAdvice = '';
          let exerciseAdvice = '';
          let sleepAdvice = '';
          let doctorAdvice = '';

          try {
            const recResponse = await healthRecommendationAPI.getDraft(record.id);
            if (recResponse.status === 'success' && recResponse.data) {
              approvalStatus = recResponse.data.approval_status || 'pending';
              approvedAt = recResponse.data.approved_at;
              nutritionAdvice = recResponse.data.nutrition_advice || '';
              exerciseAdvice = recResponse.data.exercise_advice || '';
              sleepAdvice = recResponse.data.sleep_advice || '';
              doctorAdvice = recResponse.data.doctor_advice || '';
            }
          } catch (err) {
            console.log('No recommendations yet for record', record.id);
          }

          return {
            id: record.id,
            no: index + 1,
            date: formatDate(record.visit_date),
            patientName: `${record.prename || ''}${record.firstname || ''} ${record.lastname || ''}`.trim() || '-',
            gender: labData.sex || '-',
            age: labData.age || '-',
            diseases: healthData.diseases || 'ไม่มี',
            company: healthData.company || '-',
            foodAllergies: labData['food allergies'] || '-',
            hn: record.hn || '-',  // ใช้ HN จาก record แทน NID
            parameters: parameters,
            templates: healthData.templates || [],
            matchedTemplates: healthData.matched_templates || [],
            tags: healthData.tags || [],
            approvalStatus: approvalStatus,
            approvedAt: approvedAt,
            isApproved: approvalStatus === 'approved',
            // ข้อมูลเพิ่มเติมสำหรับ PDF
            labData: labData, // ข้อมูล lab ทั้งหมด
            weight: labData['body weight'] || '-',
            height: labData['height'] || '-',
            bmi: labData['bmi'] || '-',
            bloodPressure: (labData['bps'] && labData['bpd']) ? `${labData['bps']}/${labData['bpd']}` : '-',
            pulse: labData['pulse'] || '-',
            doctorNote: doctorAdvice || labData['doctor note'] || '-',
            // คำแนะนำจาก AI
            nutritionAdvice: nutritionAdvice,
            exerciseAdvice: exerciseAdvice,
            sleepAdvice: sleepAdvice
          };
        }));

        setHealthRecords(formattedData);
        setAllHealthRecords(formattedData); // เก็บข้อมูลทั้งหมดไว้สำหรับ filter
      } else {
        // ถ้าไม่มีข้อมูล ให้แสดงเป็น array ว่าง
        setHealthRecords([]);
        setAllHealthRecords([]);
      }
    } catch (err) {
      console.error('Error fetching health records:', err);
      // ถ้า error ให้แสดงเป็น array ว่าง
      setHealthRecords([]);
      setAllHealthRecords([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <DatePickerStyles />
      <PageContainer>
        <PageTitle>สมุดสุขภาพ</PageTitle>

        <FilterSection>
          {/* แถวที่ 1 - Labels */}
          <GridItem column="1" row="1">
            <Label>วันที่ตรวจเริ่ม</Label>
          </GridItem>
          <GridItem column="2" row="1">
            <Label>ถึง</Label>
          </GridItem>
          <GridItem column="3" row="1">
            <Label>ค้นหาชื่อ-สกุล, HN</Label>
          </GridItem>
          <GridItem column="4" row="1">
            <Label>บริษัท</Label>
          </GridItem>
          <GridItem column="5" row="1">
            <Label>สถานะ</Label>
          </GridItem>

          {/* แถวที่ 2 - Input Fields และ Buttons */}
          <GridItem column="1" row="2">
            <DateInputContainer>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="2" row="2">
            <DateInputContainer>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
                minDate={startDate}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="3" row="2">
            <TextInput
              type="text"
              placeholder="พิมพ์ชื่อหรือ HN"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </GridItem>

          <GridItem column="4" row="2">
            <TextInput
              type="text"
              placeholder="ค้นหาบริษัท"
              value={searchCompany}
              onChange={(e) => setSearchCompany(e.target.value)}
            />
          </GridItem>

          <GridItem column="5" row="2">
            <SelectInput
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">เลือกสถานะ</option>
              <option value="รอการอนุมัติ">รอการอนุมัติ</option>
              <option value="อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว">อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว</option>
              <option value="มีการแก้ไขรอการอนุมัติอีกครั้ง">มีการแก้ไขรอการอนุมัติอีกครั้ง</option>
            </SelectInput>
          </GridItem>

          <GridItem column="6" row="2">
            <SearchButton onClick={handleSearch}>
              ค้นหา
            </SearchButton>
          </GridItem>

          <GridItem column="7" row="2">
            <PrintButton onClick={handlePrint}>
              <Printer size={16} />
              Print
            </PrintButton>
          </GridItem>
        </FilterSection>

        <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell rowSpan="2" style={{ width: '50px' }}>ที่</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '100px' }}>วันที่ตรวจ</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '180px' }}>ชื่อผู้รับการตรวจ/เพศ/อายุ/โรคประจำตัว</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '120px' }}>HN</TableHeaderCell>
                <TableHeaderCell colSpan="4">ผลการตรวจสุขภาพ</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '200px' }}>การแปลผลสุขภาพที่กำหนดไว้เบื้องต้น/ Doctor Note</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '150px' }}>การจัดการข้อมูล</TableHeaderCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell2 style={{ width: '150px' }}>พารามิเตอร์</TableHeaderCell2>
                <TableHeaderCell2 style={{ width: '150px' }}>ค่าปกติ</TableHeaderCell2>
                <TableHeaderCell2 style={{ width: '120px' }}>ค่าที่ตรวจวัดได้</TableHeaderCell2>
                <TableHeaderCell2 style={{ width: '50px' }}></TableHeaderCell2>
              </TableRow>
            </TableHeader>
            <tbody>
              {healthRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{record.no}</TableCell>
                  <TableCell>{record.date}</TableCell>
                  <TableCell style={{ textAlign: 'left' }}>
                    <div><span style={{ fontWeight: '700' }}>ชื่อ:</span> <span style={{ fontWeight: '400' }}>{record.patientName}</span></div>
                    <div><span style={{ fontWeight: '700' }}>เพศ:</span> {record.gender}</div>
                    <div><span style={{ fontWeight: '700' }}>อายุ:</span> {record.age} ปี</div>
                    <div><span style={{ fontWeight: '700' }}>โรคประจำตัว:</span></div>
                    <div style={{ fontWeight: '400' }}>{record.diseases}</div>
                    <div><span style={{ fontWeight: '700' }}>บริษัท:</span> <span style={{ fontWeight: '400' }}>{record.company}</span></div>
                  </TableCell>
                  <TableCell>{record.hn}</TableCell>
                  <TableCell colSpan="4" style={{ padding: 0 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {record.parameters.map((param, index) => {
                          const comparison = compareValues(param.normal, param.result);
                          return (
                            <tr key={index}>
                              <td style={{
                                padding: '0.125rem 1rem',
                                textAlign: 'left',
                                fontSize: '14px',
                                color: '#6F6F6F',
                                width: '150px'
                              }}>
                                {param.name}
                              </td>
                              <td style={{
                                padding: '0.125rem 1rem',
                                textAlign: 'center',
                                fontSize: '14px',
                                color: '#6F6F6F',
                                whiteSpace: 'nowrap',
                                width: '150px'
                              }}>
                                {param.normal}
                              </td>
                              <td style={{
                                padding: '0.125rem 0.5rem',
                                textAlign: 'center',
                                fontSize: '14px',
                                color: '#6F6F6F',
                                width: '120px'
                              }}>
                                {param.result}
                              </td>
                              <td style={{
                                padding: '0.125rem 0.25rem',
                                textAlign: 'left',
                                fontSize: '14px',
                                color: '#6F6F6F',
                                width: '50px'
                              }}>
                                <ComparisonBadge status={comparison.status}>
                                  {comparison.label}
                                </ComparisonBadge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </TableCell>
                  <TableCell style={{ textAlign: 'left' }}>
                    {/* แสดง Templates */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
                      {record.templates.map((templateName, index) => {
                        // หา matched template data ที่ตรงกับ template name นี้
                        const matchedTemplate = record.matchedTemplates?.find(
                          t => t.template_name === templateName
                        );

                        return (
                          <TemplateBadge
                            key={index}
                            disabled={record.isApproved}
                            onClick={() => handleTemplateClick(templateName, matchedTemplate)}
                          >
                            <TemplateBadgeText>{templateName}</TemplateBadgeText>
                            {!record.isApproved && (
                              <RemoveButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveTemplate(record.id, index);
                                }}
                                title="ลบ template"
                              >
                                <X size={8} />
                              </RemoveButton>
                            )}
                          </TemplateBadge>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <EditButton
                          disabled={record.isApproved}
                          onClick={() => {
                            if (!record.isApproved) {
                              handleEdit(record.id);
                            }
                          }}
                        >
                          แก้ไข
                        </EditButton>
                        {record.isApproved ? (
                          <ResendButton onClick={() => handleSendResult(record.id)}>
                            ส่งใหม่
                          </ResendButton>
                        ) : (
                          <SendResultButton onClick={() => handleSendResult(record.id)}>
                            กดเพื่อส่งผลสุขภาพ
                          </SendResultButton>
                        )}
                      </div>
                      {record.isApproved && (
                        <HistoryButton onClick={() => handleViewHistory(record.id)}>
                          <History />
                          ประวัติการส่งผลสุขภาพ
                        </HistoryButton>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
                      <ViewDataButton onClick={() => handleViewData(record.id)}>
                        ดูข้อมูล
                      </ViewDataButton>
                      <StatusText>สถานะจัดส่ง</StatusText>
                      <StatusBadge status={
                        record.approvalStatus === 'approved' ? 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว' :
                        record.approvalStatus === 'revised' ? 'มีการแก้ไขรอการอนุมัติอีกครั้ง' :
                        'รอการอนุมัติ'
                      }>
                        {record.approvalStatus === 'approved' ? 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว' :
                         record.approvalStatus === 'revised' ? 'มีการแก้ไขรอการอนุมัติอีกครั้ง' :
                         'รอการอนุมัติ'}
                      </StatusBadge>
                      <ActionPrintButton
                        onClick={() => handlePrint(record.id)}
                        disabled={!record.isApproved}
                      >
                        <Printer size={14} />
                        Print
                      </ActionPrintButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </PageContainer>

      <TemplateDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        templateData={selectedTemplate}
      />

      <EditTemplateModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        recordId={selectedRecordId}
        selectedRecord={selectedRecord}
        onSave={fetchHealthRecords}
      />

      <HealthResultModal
        isOpen={isHealthResultModalOpen}
        onClose={handleCloseHealthResultModal}
        selectedRecord={selectedRecord}
        onApprove={handleApprove}
      />

      <SendHealthResultModal
        isOpen={isSendHealthResultModalOpen}
        onClose={handleCloseSendHealthResultModal}
        onConfirm={handleConfirmSendAgain}
      />

      <HealthReportModal
        isOpen={isHealthReportModalOpen}
        onClose={handleCloseHealthReportModal}
        recordData={selectedRecord}
      />

      {/* Hidden Templates Modal */}
      {isHiddenTemplatesModalOpen && (
        <HiddenTemplatesModal
          isOpen={isHiddenTemplatesModalOpen}
          onClose={() => setIsHiddenTemplatesModalOpen(false)}
          hiddenTemplates={hiddenTemplates}
          onRestore={handleRestoreTemplate}
        />
      )}
    </Layout>
  );
};

// Hidden Templates Modal Component
const HiddenTemplatesModal = ({ isOpen, onClose, hiddenTemplates, onRestore }) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Templates ที่ซ่อนไว้</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {hiddenTemplates.length === 0 ? (
            <EmptyMessage>ไม่มี Templates ที่ซ่อนไว้</EmptyMessage>
          ) : (
            <HiddenTemplatesList>
              {hiddenTemplates.map((template) => (
                <HiddenTemplateItem key={template.template_id}>
                  <TemplateInfo>
                    <TemplateName>{template.template_name}</TemplateName>
                    <TemplateDetails>
                      <div>ซ่อนเมื่อ: {new Date(template.hidden_at).toLocaleString('th-TH')}</div>
                      {template.tags && template.tags.length > 0 && (
                        <TagsContainer>
                          {template.tags.map((tag, index) => (
                            <Tag key={index}>{tag}</Tag>
                          ))}
                        </TagsContainer>
                      )}
                    </TemplateDetails>
                  </TemplateInfo>
                  <RestoreButton onClick={() => onRestore(template.template_id)}>
                    เอากลับมา
                  </RestoreButton>
                </HiddenTemplateItem>
              ))}
            </HiddenTemplatesList>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #E5E7EB;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6B7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #111827;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #6B7280;
  padding: 2rem;
`;

const HiddenTemplatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HiddenTemplateItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
  background-color: #F9FAFB;
`;

const TemplateInfo = styled.div`
  flex: 1;
`;

const TemplateName = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const TemplateDetails = styled.div`
  font-size: 0.875rem;
  color: #6B7280;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  background-color: #E0F2F1;
  color: #00796B;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
`;

const RestoreButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #009FA2;
  }
`;

export default HealthBook;